import React, { useMemo, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useFormik } from 'formik';
import yupSchemaToFields from '../../../../../../../helpers/yupSchemaToFields';

import SidePopup from '../../../../SidePopup';
import TextInput from '../../../../../../Form/TextInput';
import PhoneInput from '../../../../../../Form/PhoneInput';

import classes from './styles.module.scss';

export default function ManagerSidePanel({
  isVisible,
  handleClose,
  onSave,
  initialValues,
  isEdit,
}) {
  const { t } = useTranslation();
  const [hasTriedToSubmit, setHasTriedToSubmit] = useState(false);

  const validationSchema = useMemo(
    () =>
      yup.object({
        firstName: yup
          .string()
          .trim()
          .matches(
            /^[A-Za-z^]*$/,
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.specialCharactersNotAllowed'
            )
          )
          .required(
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.required'
            )
          ),
        lastName: yup
          .string()
          .trim()
          .matches(
            /^[A-Za-z^]*$/,
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.specialCharactersNotAllowed'
            )
          )
          .required(
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.required'
            )
          ),
        email: yup
          .string()
          .trim()
          .email(
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.validEmail'
            )
          )
          .required(
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.required'
            )
          ),
        designation: yup
          .string()
          .trim()
          .nullable()
          .optional()
          .max(
            100,
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.designationMaxLength'
            )
          ),
        phone: yup
          .string()
          .required(
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.required'
            )
          )
          .test(
            'is-valid-phone',
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.phoneInvalid'
            ),
            async (value) => {
              if (!value) {
                return true; // Skip validation if the value is empty
              }

              const { isValidPhoneNumber, AsYouType } = await import(
                'libphonenumber-js'
              );

              const formattedNumber = new AsYouType().input(value);

              const formattedNumberWithPlus = formattedNumber.startsWith('+')
                ? formattedNumber
                : `+${formattedNumber}`;

              return isValidPhoneNumber(
                new AsYouType().input(formattedNumberWithPlus)
              );
            }
          ),
      }),
    [t]
  );

  const formik = useFormik({
    initialValues: isEdit
      ? {
          firstName: initialValues?.firstName || '',
          lastName: initialValues?.lastName || '',
          email: initialValues?.email || '',
          phone: initialValues?.phone || '',
          designation: initialValues?.designation || '',
        }
      : {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          designation: '',
        },
    validationSchema,
    onSubmit: (values) => {
      onSave(values);
      handleClose();
    },
    enableReinitialize: true,
  });

  // Ensure add-mode form is empty on open
  useEffect(() => {
    if (isVisible && !isEdit) {
      setHasTriedToSubmit(false);
      formik.resetForm({
        values: {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          designation: '',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, isEdit]);

  const onClose = () => {
    setHasTriedToSubmit(false);
    handleClose();
  };

  const resetForm = () => {
    formik.resetForm();
    setHasTriedToSubmit(false);
  };

  return (
    <SidePopup
      fields={[...yupSchemaToFields(validationSchema)]}
      formik={{ ...formik, resetForm, dirty: formik.dirty }}
      mode={isEdit ? 'edit' : 'add'}
      isVisible={isVisible}
      handleClose={onClose}
      title={
        isEdit
          ? t(
              'dashboardComponents.Console.CompanyManagement.addEdit.managerPanel.titleEdit'
            )
          : t(
              'dashboardComponents.Console.CompanyManagement.addEdit.managerPanel.titleAdd'
            )
      }
      leftButtonLabel={t('common.reset')}
      rightButtonLabel={isEdit ? t('common.save') : t('common.add')}
      onLeftButtonClick={resetForm}
      onRightButtonClick={() => {
        setHasTriedToSubmit(true);
        formik.handleSubmit();
      }}
    >
      <div className={classes.ManagerSidePanel}>
        <div className={classes.row}>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.Manager First Name'
              )}
              touched={hasTriedToSubmit}
              value={formik.values.firstName}
              height={50}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="firstName"
              error={formik.errors.firstName}
            />
          </div>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.Manager Last Name'
              )}
              value={formik.values.lastName}
              height={50}
              touched={hasTriedToSubmit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="lastName"
              error={formik.errors.lastName}
            />
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.Designation'
              )}
              touched={hasTriedToSubmit}
              value={formik.values.designation}
              height={50}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="designation"
              error={formik.errors.designation}
              note={t('common.optional')}
            />
          </div>
          <div className={classes.col}>
            <PhoneInput
              name="phone"
              touched={hasTriedToSubmit}
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.Phone'
              )}
              error={formik.errors.phone}
              setFieldValue={formik.setFieldValue}
              value={formik.values.phone}
              height={50}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.Manager Email'
              )}
              touched={hasTriedToSubmit}
              value={formik.values.email}
              height={50}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="email"
              error={formik.errors.email}
            />
          </div>
        </div>
      </div>
    </SidePopup>
  );
}
